/**
 * Hook to monitor JWT token expiration
 * Shows warning 1 minute before expiration and handles auto-logout
 */

import { useEffect, useState, useRef } from 'react';
import { AuthLogic } from '@/modules/iam';
import { JWTLogic } from '@/modules/iam/logic';
import { useUserContext } from '@/modules/user/useUserContext';
import { ClaimsLogic } from './logic';

interface TokenExpirationState {
  showWarning: boolean;
  timeUntilExpiration: number | undefined;
  isExpired: boolean;
}

const WARNING_TIME_MS = 60 * 1000; // 1 minute before expiration
const CHECK_INTERVAL_MS = 1000; // Check every second

export function useTokenExpiration(): TokenExpirationState {
  const [showWarning, setShowWarning] = useState(false);
  const [timeUntilExpiration, setTimeUntilExpiration] = useState<
    number | undefined
  >(undefined);
  const [isExpired, setIsExpired] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { claims } = useUserContext();

  const checkTokenExpiration = async () => {
    const authService = AuthLogic.getAuthService();
    const isAuthenticated = await authService.isAuthenticated();

    if (!isAuthenticated) {
      // Not authenticated - clear state
      setShowWarning(false);
      setTimeUntilExpiration(undefined);
      setIsExpired(false);
      return;
    }

    // Get token - try Bearer token first, then check stored token
    let token: string | null = null;

    // Try to get Bearer token from auth service
    const authToken = await authService.getToken();
    if (authToken && authToken !== 'http-only-cookie') {
      token = authToken;
    } else {
      // For PWA, try to get Bearer token from localStorage
      // NativeAuthService will throw an error, which we'll catch below
      try {
        token = await authService.getBearerToken();
      } catch (error) {
        // NativeAuthService doesn't support Bearer tokens - that's expected
        token = null;
      }
    }

    if (!token) {
      // No token available - might be using HTTP-only cookies
      // Get latest trust level from server to verify authentication status
      const currentUser = await authService.getCurrentUser();
      if (currentUser) {
        const earliestExpiration = ClaimsLogic.earliestExpiration(
          currentUser.claims
        );
        // For HTTP-only cookies, we can't decode the token
        // Use a conservative approach: assume expiration after 24 hours from login
        // This is not ideal, but HTTP-only cookies can't be decoded client-side
        setShowWarning(false);
        if (earliestExpiration) {
          const timeUntil = earliestExpiration.getTime() - Date.now();
          setTimeUntilExpiration(timeUntil);
          setIsExpired(false);
          if (timeUntil <= WARNING_TIME_MS) {
            // Token will expire soon - show warning
            setShowWarning(true);
          }
        } else {
          setTimeUntilExpiration(undefined);
          setIsExpired(true);
        }
      } else {
        setShowWarning(false);
        setTimeUntilExpiration(undefined);
        setIsExpired(true);
      }
      return;
    }

    // Decode and check expiration
    const jwt = JWTLogic.decodeJwt(token);
    const timeUntil = JWTLogic.getTimeUntilExpiration(jwt);
    setTimeUntilExpiration(timeUntil);

    if (timeUntil === undefined) {
      // JWT has no exp claim — token never expires (e.g., UNVERIFIED guest tokens)
      setIsExpired(false);
      setShowWarning(false);
      return;
    }

    if (timeUntil <= 0) {
      // Token is expired
      setIsExpired(true);
      setShowWarning(false);
    } else if (timeUntil <= WARNING_TIME_MS) {
      // Token will expire soon - show warning
      setIsExpired(false);
      setShowWarning(true);
    } else {
      // Token is still valid
      setIsExpired(false);
      setShowWarning(false);
    }
  };

  // Initial check and set up interval to check periodically
  useEffect(() => {
    // Initial check
    checkTokenExpiration();

    // Set up interval to check periodically
    intervalRef.current = setInterval(checkTokenExpiration, CHECK_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-check expiration when claims change (e.g., after token refresh)
  useEffect(() => {
    checkTokenExpiration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claims]);

  return {
    showWarning,
    timeUntilExpiration,
    isExpired,
  };
}
